import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, 
  DownloadSimple, 
  CalendarBlank,
  CurrencyDollar,
  Briefcase,
  ShieldCheck,
  File,
  SpinnerGap,
  CheckCircle,
  Funnel,
  Users,
  ClipboardText,
  ChartBar,
  ArrowLeft
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import { BreadcrumbsBox } from '../components/Breadcrumbs';
import { reportsAPI } from '../api/api';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';

const Reports = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState({});
  const [filters, setFilters] = useState({
    startDate: format(startOfMonth(subMonths(new Date(), 3)), 'yyyy-MM-dd'),
    endDate: format(endOfMonth(new Date()), 'yyyy-MM-dd'),
    jobStatus: 'all',
    escrowStatus: 'all',
    applicationStatus: 'all'
  });

  const userType = localStorage.getItem('userType');
  const isContractor = userType === 'contractor';
  const isWorker = userType === 'worker';

  const handleDownload = async (reportType, reportFormat) => {
    const loadingKey = `${reportType}_${reportFormat}`;
    setLoading(prev => ({ ...prev, [loadingKey]: true }));

    try {
      let response;
      const startDate = filters.startDate || undefined;
      const endDate = filters.endDate || undefined;

      switch (reportType) {
        case 'earnings':
          response = await reportsAPI.exportEarnings(reportFormat, startDate, endDate);
          break;
        case 'jobs':
          const jobStatus = filters.jobStatus === 'all' ? undefined : filters.jobStatus;
          response = await reportsAPI.exportJobs(reportFormat, jobStatus, startDate, endDate);
          break;
        case 'payments':
          response = await reportsAPI.exportPayments(reportFormat, startDate, endDate);
          break;
        case 'escrow':
          const escrowStatus = filters.escrowStatus === 'all' ? undefined : filters.escrowStatus;
          response = await reportsAPI.exportEscrow(reportFormat, escrowStatus);
          break;
        case 'hiring':
          response = await reportsAPI.exportHiring(reportFormat, startDate, endDate);
          break;
        case 'applications':
          const appStatus = filters.applicationStatus === 'all' ? undefined : filters.applicationStatus;
          response = await reportsAPI.exportApplications(reportFormat, appStatus, startDate, endDate);
          break;
        case 'work-history':
          response = await reportsAPI.exportWorkHistory(reportFormat, startDate, endDate);
          break;
        default:
          throw new Error('Unknown report type');
      }

      // Create download link
      const blob = new Blob([response.data], { 
        type: reportFormat === 'pdf' ? 'application/pdf' : 'text/csv' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${reportType}_report_${format(new Date(), 'yyyyMMdd')}.${reportFormat}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        title: 'Download Complete',
        description: `${reportType.charAt(0).toUpperCase() + reportType.slice(1).replace('-', ' ')} report downloaded successfully!`
      });
    } catch (error) {
      console.error('Download failed:', error);
      toast({
        title: 'Download Failed',
        description: 'Failed to download report. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoading(prev => ({ ...prev, [loadingKey]: false }));
    }
  };

  // Define all report cards
  const allReportCards = [
    // Worker reports
    {
      id: 'earnings',
      title: 'Earnings Report',
      description: 'Export your earnings from invoices and escrow payments',
      icon: CurrencyDollar,
      color: 'bg-emerald-500',
      borderColor: 'border-emerald-600',
      available: isWorker
    },
    {
      id: 'work-history',
      title: 'Work History',
      description: 'Export completed jobs with ratings and earnings summary',
      icon: ChartBar,
      color: 'bg-indigo-500',
      borderColor: 'border-indigo-600',
      available: isWorker
    },
    {
      id: 'applications',
      title: 'Application History',
      description: 'Export all your job applications with status and details',
      icon: ClipboardText,
      color: 'bg-cyan-500',
      borderColor: 'border-cyan-600',
      available: isWorker,
      hasFilter: true,
      filterKey: 'applicationStatus',
      filterOptions: [
        { value: 'all', label: 'All Statuses' },
        { value: 'pending', label: 'Pending' },
        { value: 'accepted', label: 'Accepted' },
        { value: 'rejected', label: 'Rejected' }
      ]
    },
    // Contractor reports
    {
      id: 'hiring',
      title: 'Hiring Report',
      description: 'Export all workers you have hired with job details',
      icon: Users,
      color: 'bg-violet-500',
      borderColor: 'border-violet-600',
      available: isContractor
    },
    // Shared reports
    {
      id: 'jobs',
      title: 'Jobs Report',
      description: isContractor 
        ? 'Export all your posted jobs with status and applications'
        : 'Export all jobs you have applied to',
      icon: Briefcase,
      color: 'bg-blue-500',
      borderColor: 'border-blue-600',
      available: true,
      hasFilter: true,
      filterKey: 'jobStatus',
      filterOptions: [
        { value: 'all', label: 'All Statuses' },
        { value: 'open', label: 'Open' },
        { value: 'filled', label: 'Filled' },
        { value: 'closed', label: 'Closed' }
      ]
    },
    {
      id: 'escrow',
      title: 'Escrow Report',
      description: 'Export all your escrow transactions and milestones',
      icon: ShieldCheck,
      color: 'bg-amber-500',
      borderColor: 'border-amber-600',
      available: true,
      hasFilter: true,
      filterKey: 'escrowStatus',
      filterOptions: [
        { value: 'all', label: 'All Statuses' },
        { value: 'pending', label: 'Pending' },
        { value: 'funded', label: 'Funded' },
        { value: 'released', label: 'Released' },
        { value: 'disputed', label: 'Disputed' }
      ]
    },
    {
      id: 'payments',
      title: 'Payment History',
      description: 'Export your subscription and payment transactions',
      icon: FileText,
      color: 'bg-purple-500',
      borderColor: 'border-purple-600',
      available: true
    }
  ];

  const reportCards = allReportCards.filter(r => r.available);

  return (
    <div className="min-h-screen bg-neutral-100 dark:bg-neutral-900 py-8" data-testid="reports-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumbs */}
        <BreadcrumbsBox 
          customItems={[
            { label: 'Home', path: userType === 'worker' ? '/worker-dashboard' : '/contractor-dashboard', isHome: true },
            { label: 'Reports', path: '/reports', isCurrent: true }
          ]}
          className="mb-6"
        />
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-[#2B4C6F] to-blue-700 rounded-xl flex items-center justify-center border-2 border-black shadow-[4px_4px_0px_#000]">
              <DownloadSimple size={32} weight="bold" className="text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-black text-neutral-900 dark:text-white">
                Export Reports
              </h1>
              <p className="text-neutral-600 dark:text-neutral-400 font-medium">
                Download your data as professional PDF or CSV files
              </p>
            </div>
          </div>
        </div>

        {/* Date Range Filter */}
        <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-6 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F] mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Funnel size={24} weight="bold" className="text-[#2B4C6F]" />
            <h2 className="text-lg font-bold text-neutral-900 dark:text-white">Date Range Filter</h2>
          </div>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4">
            Select a date range for your reports (applies to most report types)
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="startDate" className="font-bold text-neutral-700 dark:text-neutral-300">Start Date</Label>
              <Input
                id="startDate"
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                className="mt-1 border-2 border-neutral-200 dark:border-neutral-600 h-12"
                data-testid="report-start-date"
              />
            </div>
            <div>
              <Label htmlFor="endDate" className="font-bold text-neutral-700 dark:text-neutral-300">End Date</Label>
              <Input
                id="endDate"
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                className="mt-1 border-2 border-neutral-200 dark:border-neutral-600 h-12"
                data-testid="report-end-date"
              />
            </div>
          </div>
        </div>

        {/* Report Cards */}
        <div className="grid md:grid-cols-2 gap-6">
          {reportCards.map((report) => {
            const IconComponent = report.icon;
            const isPdfLoading = loading[`${report.id}_pdf`];
            const isCsvLoading = loading[`${report.id}_csv`];
            
            return (
              <div 
                key={report.id} 
                className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl overflow-hidden shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F] hover:shadow-[6px_6px_0px_#000] dark:hover:shadow-[6px_6px_0px_#2B4C6F] transition-all"
                data-testid={`report-card-${report.id}`}
              >
                {/* Header */}
                <div className={`${report.color} p-4 border-b-2 border-black dark:border-neutral-700`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                        <IconComponent size={24} weight="bold" className="text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-black text-white">{report.title}</h3>
                        <span className="text-xs font-bold text-white/80">PDF & CSV</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Content */}
                <div className="p-5">
                  <p className="text-neutral-600 dark:text-neutral-400 mb-4">{report.description}</p>
                  
                  {/* Optional Filter */}
                  {report.hasFilter && (
                    <div className="mb-4">
                      <Label htmlFor={report.filterKey} className="text-sm font-bold text-neutral-700 dark:text-neutral-300">
                        Filter by Status
                      </Label>
                      <Select 
                        value={filters[report.filterKey]} 
                        onValueChange={(value) => setFilters(prev => ({ ...prev, [report.filterKey]: value }))}
                      >
                        <SelectTrigger className="mt-1 h-10 border-2 border-neutral-200 dark:border-neutral-600" data-testid={`${report.id}-status-filter`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {report.filterOptions.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  {/* Download Buttons */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleDownload(report.id, 'pdf')}
                      disabled={isPdfLoading || isCsvLoading}
                      className="flex-1 h-12 flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 text-white font-bold rounded-lg border-2 border-black shadow-[2px_2px_0px_#000] active:translate-y-1 active:shadow-none transition-all disabled:opacity-50"
                      data-testid={`download-${report.id}-pdf`}
                    >
                      {isPdfLoading ? (
                        <SpinnerGap size={20} className="animate-spin" />
                      ) : (
                        <File size={20} weight="bold" />
                      )}
                      PDF
                    </button>
                    <button
                      onClick={() => handleDownload(report.id, 'csv')}
                      disabled={isPdfLoading || isCsvLoading}
                      className="flex-1 h-12 flex items-center justify-center gap-2 bg-white dark:bg-neutral-700 text-emerald-600 dark:text-emerald-400 font-bold rounded-lg border-2 border-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 shadow-[2px_2px_0px_#10b981] active:translate-y-1 active:shadow-none transition-all disabled:opacity-50"
                      data-testid={`download-${report.id}-csv`}
                    >
                      {isCsvLoading ? (
                        <SpinnerGap size={20} className="animate-spin" />
                      ) : (
                        <FileText size={20} weight="bold" />
                      )}
                      CSV
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Info Section */}
        <div className="mt-8 bg-gradient-to-r from-[#2B4C6F]/10 to-blue-500/10 dark:from-[#2B4C6F]/20 dark:to-blue-500/20 rounded-xl p-6 border-2 border-[#2B4C6F]/30">
          <h3 className="font-bold text-neutral-900 dark:text-white mb-3 flex items-center gap-2">
            <CheckCircle size={24} weight="bold" className="text-[#2B4C6F]" />
            About Your Reports
          </h3>
          <ul className="space-y-2 text-sm text-neutral-600 dark:text-neutral-400">
            <li className="flex items-start gap-2">
              <span className="text-[#2B4C6F] font-bold">•</span>
              <span><strong className="text-neutral-900 dark:text-white">PDF Reports</strong> are professionally formatted with summaries and ready for printing or sharing</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#2B4C6F] font-bold">•</span>
              <span><strong className="text-neutral-900 dark:text-white">CSV Reports</strong> can be imported into Excel, Google Sheets, or accounting software</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#2B4C6F] font-bold">•</span>
              <span>All reports include a summary section with totals and key metrics</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#2B4C6F] font-bold">•</span>
              <span>All monetary amounts are displayed in USD</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Reports;
